import { AlertTriangle, Loader2, X } from 'lucide-react';
import { useState } from 'react';
import { trpc } from '~/api/trpc';
import { Alert, AlertDescription } from '~/components/ui/alert';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';

interface MergeCustomersDialogProps {
    open: boolean;
    onClose: () => void;
    onMergeComplete?: () => void;
}

type MergeField = 'id' | 'email' | 'firstName' | 'lastName' | 'phone' | 'birthday' | 'gender' | 'city' | 'zipCode' | 'street' | 'streetNumber' | 'country' | 'category' | 'note' | 'praxismanagerId' | 'originalShoreId';

const MergeCustomersDialog = ({ open, onClose, onMergeComplete }: MergeCustomersDialogProps) => {
    const [customerASearchQuery, setCustomerASearchQuery] = useState('');
    const [customerBSearchQuery, setCustomerBSearchQuery] = useState('');
    const [customerAId, setCustomerAId] = useState<number | null>(null);
    const [customerBId, setCustomerBId] = useState<number | null>(null);
    const [fieldChoices, setFieldChoices] = useState<Record<MergeField, 'A' | 'B'>>({
        id: 'B', // Which customer ID to keep (A or B)
        email: 'B',
        firstName: 'B',
        lastName: 'B',
        phone: 'B',
        birthday: 'B',
        gender: 'B',
        city: 'B',
        zipCode: 'B',
        street: 'B',
        streetNumber: 'B',
        country: 'B',
        category: 'B',
        note: 'B',
        praxismanagerId: 'B',
        originalShoreId: 'B',
    });

    const customerASearchResults = trpc.customers.search.useQuery(
        { query: customerASearchQuery, limit: 10 },
        { enabled: customerASearchQuery.length > 1 }
    );

    const customerBSearchResults = trpc.customers.search.useQuery(
        { query: customerBSearchQuery, limit: 10 },
        { enabled: customerBSearchQuery.length > 1 }
    );

    const customerA = trpc.customers.getOne.useQuery(
        { id: customerAId! },
        { enabled: !!customerAId }
    );

    const customerB = trpc.customers.getOne.useQuery(
        { id: customerBId! },
        { enabled: !!customerBId }
    );

    const mergeMutation = trpc.customers.merge.useMutation({
        onSuccess: () => {
            onMergeComplete?.();
            handleClose();
        },
    });

    const handleClose = () => {
        setCustomerASearchQuery('');
        setCustomerBSearchQuery('');
        setCustomerAId(null);
        setCustomerBId(null);
        setFieldChoices({
            id: 'B',
            email: 'B',
            firstName: 'B',
            lastName: 'B',
            phone: 'B',
            birthday: 'B',
            gender: 'B',
            city: 'B',
            zipCode: 'B',
            street: 'B',
            streetNumber: 'B',
            country: 'B',
            category: 'B',
            note: 'B',
            praxismanagerId: 'B',
            originalShoreId: 'B',
        });
        onClose();
    };

    const handleMerge = () => {
        if (!customerAId || !customerBId) {
            return;
        }

        mergeMutation.mutate({
            customerAId,
            customerBId,
            fieldChoices,
        });
    };

    const canMerge = customerAId && customerBId && customerAId !== customerBId && !mergeMutation.isPending;

    const customerAData = customerA.data?.data;
    const customerBData = customerB.data?.data;

    // Determine which customer will be deleted based on ID choice
    const deletedCustomerLabel = fieldChoices.id === 'A' ? 'B' : 'A';
    const keptCustomerLabel = fieldChoices.id === 'A' ? 'A' : 'B';

    const fieldLabels: Record<MergeField, string> = {
        id: 'Kunden-ID (welcher Kunde bleibt bestehen)',
        email: 'E-Mail',
        firstName: 'Vorname',
        lastName: 'Nachname',
        phone: 'Telefon',
        birthday: 'Geburtstag',
        gender: 'Geschlecht',
        city: 'Stadt',
        zipCode: 'PLZ',
        street: 'Straße',
        streetNumber: 'Hausnummer',
        country: 'Land',
        category: 'Kategorie',
        note: 'Notiz',
        praxismanagerId: 'Praxismanager ID',
        originalShoreId: 'Original Shore ID',
    };

    return (
        <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
            <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-bold">Kunden zusammenführen</DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground">
                        Wählen Sie zwei Kunden aus, die zusammengeführt werden sollen. Sie können für jedes Feld einzeln wählen, welcher Wert übernommen werden soll.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Customer A Selection */}
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700">Kunde A</Label>
                        <Input
                            placeholder="Kunde A suchen..."
                            value={customerASearchQuery}
                            onChange={(e) => setCustomerASearchQuery(e.target.value)}
                            className="focus:ring-primary"
                        />
                        {customerASearchQuery.length > 1 && (
                            <div className="border border-gray-200 rounded-md max-h-48 overflow-y-auto bg-white shadow-sm">
                                {customerASearchResults.isLoading ? (
                                    <div className="p-4 text-center text-xs text-muted-foreground">
                                        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                                        Suche...
                                    </div>
                                ) : customerASearchResults.data?.data?.length === 0 ? (
                                    <div className="p-4 text-center text-xs text-muted-foreground italic">
                                        Keine Ergebnisse
                                    </div>
                                ) : (
                                    customerASearchResults.data?.data?.map((customer) => (
                                        <div
                                            key={customer.id}
                                            className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-200 last:border-b-0 transition-colors"
                                            onClick={() => {
                                                setCustomerAId(customer.id);
                                                setCustomerASearchQuery('');
                                            }}
                                        >
                                            <div className="font-medium text-sm">
                                                {customer.firstName} {customer.lastName}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                {customer.email || 'Keine E-Mail'} • ID: {customer.id}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                        {customerAData && (
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="font-medium text-sm text-blue-900">
                                            {customerAData.firstName} {customerAData.lastName}
                                        </div>
                                        <div className="text-xs text-blue-700">
                                            {customerAData.email || 'Keine E-Mail'} • ID: {customerAData.id}
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setCustomerAId(null)}
                                        className="h-6 w-6 p-0"
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Customer B Selection */}
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700">Kunde B</Label>
                        <Input
                            placeholder="Kunde B suchen..."
                            value={customerBSearchQuery}
                            onChange={(e) => setCustomerBSearchQuery(e.target.value)}
                            className="focus:ring-primary"
                        />
                        {customerBSearchQuery.length > 1 && (
                            <div className="border border-gray-200 rounded-md max-h-48 overflow-y-auto bg-white shadow-sm">
                                {customerBSearchResults.isLoading ? (
                                    <div className="p-4 text-center text-xs text-muted-foreground">
                                        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                                        Suche...
                                    </div>
                                ) : customerBSearchResults.data?.data?.length === 0 ? (
                                    <div className="p-4 text-center text-xs text-muted-foreground italic">
                                        Keine Ergebnisse
                                    </div>
                                ) : (
                                    customerBSearchResults.data?.data?.map((customer) => (
                                        <div
                                            key={customer.id}
                                            className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-200 last:border-b-0 transition-colors"
                                            onClick={() => {
                                                setCustomerBId(customer.id);
                                                setCustomerBSearchQuery('');
                                            }}
                                        >
                                            <div className="font-medium text-sm">
                                                {customer.firstName} {customer.lastName}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                {customer.email || 'Keine E-Mail'} • ID: {customer.id}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                        {customerBData && (
                            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="font-medium text-sm text-green-900">
                                            {customerBData.firstName} {customerBData.lastName}
                                        </div>
                                        <div className="text-xs text-green-700">
                                            {customerBData.email || 'Keine E-Mail'} • ID: {customerBData.id}
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setCustomerBId(null)}
                                        className="h-6 w-6 p-0"
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Field Selection */}
                    {customerAData && customerBData && (
                        <div className="space-y-4 border-t border-gray-200 pt-4">
                            <div className="font-medium text-gray-700">Felder auswählen</div>
                            <div className="text-xs text-muted-foreground mb-4">
                                Wählen Sie für jedes Feld, welcher Wert übernommen werden soll.
                            </div>

                            <div className="space-y-3">
                                {/* ID field with special highlighting */}
                                <div className="border-2 border-amber-300 rounded-lg p-3 bg-amber-50">
                                    <Label className="text-xs font-bold text-amber-900 mb-2 flex items-center gap-2">
                                        <AlertTriangle className="h-4 w-4" />
                                        {fieldLabels['id']}
                                    </Label>
                                    <div className="text-xs text-amber-800 mb-3 italic">
                                        Diese Auswahl bestimmt, welcher Kunde gelöscht wird. Der andere Kunde bleibt bestehen.
                                    </div>
                                    <RadioGroup
                                        value={fieldChoices.id}
                                        onValueChange={(value) => {
                                            setFieldChoices(prev => ({
                                                ...prev,
                                                id: value as 'A' | 'B'
                                            }));
                                        }}
                                        className="space-y-2"
                                    >
                                        <div className="flex items-center space-x-2 bg-white rounded-md p-2 border border-gray-200">
                                            <RadioGroupItem value="A" id="id-A" />
                                            <Label htmlFor="id-A" className="cursor-pointer flex-1 text-xs">
                                                <Badge variant="outline" className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-800 border-blue-300 mr-2">
                                                    Kunde A
                                                </Badge>
                                                <span className="font-medium">ID: {customerAData.id}</span>
                                                {fieldChoices.id === 'A' && <span className="ml-2 text-green-600 font-bold">✓ Bleibt bestehen</span>}
                                                {fieldChoices.id === 'B' && <span className="ml-2 text-red-600 font-bold">✗ Wird gelöscht</span>}
                                            </Label>
                                        </div>
                                        <div className="flex items-center space-x-2 bg-white rounded-md p-2 border border-gray-200">
                                            <RadioGroupItem value="B" id="id-B" />
                                            <Label htmlFor="id-B" className="cursor-pointer flex-1 text-xs">
                                                <Badge variant="outline" className="text-xs px-1.5 py-0.5 bg-green-100 text-green-800 border-green-300 mr-2">
                                                    Kunde B
                                                </Badge>
                                                <span className="font-medium">ID: {customerBData.id}</span>
                                                {fieldChoices.id === 'B' && <span className="ml-2 text-green-600 font-bold">✓ Bleibt bestehen</span>}
                                                {fieldChoices.id === 'A' && <span className="ml-2 text-red-600 font-bold">✗ Wird gelöscht</span>}
                                            </Label>
                                        </div>
                                    </RadioGroup>
                                </div>

                                {/* Other fields */}
                                {(['email', 'firstName', 'lastName', 'phone', 'birthday', 'gender', 'city', 'zipCode', 'street', 'streetNumber', 'country', 'category', 'note', 'praxismanagerId', 'originalShoreId'] as MergeField[]).map((field) => {
                                    const valueA = customerAData[field];
                                    const valueB = customerBData[field];
                                    const currentChoice = fieldChoices[field];

                                    return (
                                        <div key={field} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                            <Label className="text-xs font-medium text-gray-700 mb-2 block">
                                                {fieldLabels[field]}
                                            </Label>
                                            <RadioGroup
                                                value={currentChoice}
                                                onValueChange={(value) => {
                                                    setFieldChoices(prev => ({
                                                        ...prev,
                                                        [field]: value as 'A' | 'B'
                                                    }));
                                                }}
                                                className="space-y-2"
                                            >
                                                <div className="flex items-center space-x-2 bg-white rounded-md p-2 border border-gray-200">
                                                    <RadioGroupItem value="A" id={`${field}-A`} />
                                                    <Label htmlFor={`${field}-A`} className="cursor-pointer flex-1 text-xs">
                                                        <Badge variant="outline" className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-800 border-blue-300 mr-2">
                                                            Kunde A
                                                        </Badge>
                                                        <span className="text-gray-900">{valueA ? String(valueA) : <span className="italic text-muted-foreground">(leer)</span>}</span>
                                                    </Label>
                                                </div>
                                                <div className="flex items-center space-x-2 bg-white rounded-md p-2 border border-gray-200">
                                                    <RadioGroupItem value="B" id={`${field}-B`} />
                                                    <Label htmlFor={`${field}-B`} className="cursor-pointer flex-1 text-xs">
                                                        <Badge variant="outline" className="text-xs px-1.5 py-0.5 bg-green-100 text-green-800 border-green-300 mr-2">
                                                            Kunde B
                                                        </Badge>
                                                        <span className="text-gray-900">{valueB ? String(valueB) : <span className="italic text-muted-foreground">(leer)</span>}</span>
                                                    </Label>
                                                </div>
                                            </RadioGroup>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Warning */}
                    {canMerge && (
                        <Alert className="border-amber-200 bg-amber-50">
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                            <AlertDescription className="text-xs text-amber-800">
                                <strong>Warnung:</strong> Kunde {deletedCustomerLabel} wird nach dem Zusammenführen gelöscht.
                                Kunde {keptCustomerLabel} bleibt bestehen und erhält alle Termine, Dokumente und andere Daten von Kunde {deletedCustomerLabel}.
                                Diese Aktion kann nicht rückgängig gemacht werden.
                            </AlertDescription>
                        </Alert>
                    )}
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={mergeMutation.isPending}
                        className="border-gray-300 hover:bg-gray-50"
                    >
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleMerge}
                        disabled={!canMerge || mergeMutation.isPending}
                        variant="destructive"
                    >
                        {mergeMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Zusammenführen
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default MergeCustomersDialog;